import React, { useMemo, useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AuthContext from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const DinnerForm = () => {
  const navigate = useNavigate();
  const {
    appStyle,
    appConfig,
    orgId,
    signedIn,
    email: userEmail,
  } = useContext(AuthContext);
  const [message, setMessage] = useState("");
  const [verified, setVerified] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMap, setErrorMap] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [email, setEmail] = useState("");
  const [verifyErrorOpen, setVerifyErrorOpen] = useState(false);

  useEffect(() => {
    const scriptId = "recaptcha-v3-script";

    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
      script.async = true;
      document.body.appendChild(script);
    }

    return () => {
      // Remove reCAPTCHA badge injected into the DOM
      const badge = document.querySelector(".grecaptcha-badge");
      if (badge && badge.parentNode) {
        badge.parentNode.removeChild(badge);
      }

      // Remove reCAPTCHA script
      const script = document.getElementById(scriptId);
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }

      // Defensive cleanup
      if (window.grecaptcha) {
        delete window.grecaptcha;
      }
    };
  }, []);

  useEffect(() => {
    if (signedIn && userEmail) {
      setEmail(userEmail);
    }
  }, [signedIn, userEmail]);

  useEffect(() => {
    if (appConfig) {
      setErrorMap({
        1001: `No email provided. This is required.`,
        1002: `Unfortunately, we cannot find you in the system. Are you sure your entered your email correctly? It must be the same email with which you purchased your ticket. Please try again or email the organizer directly at ${appConfig?.contactUsEmail}`,
        1003: `It looks like you do not have a ticket for this event. Are you sure you entered your email correctly? It must be the same email with which you purchased the ticket. Please try again or email the organizer directly at ${appConfig?.contactUsEmail}`,
        1004: `It looks like you are a little early to check in. Please try again later once the event has started.`,
        1005: `It looks like you are assigned to more than one active event. Please contact your organizer at ${appConfig?.contactUsEmail}`,
        2001: "Please enter same email with which you purchased your ticket.",
        3001: `An unknown error has occured. Please email your organizer directly at ${appConfig.contactUsEmail}`,
        4001: `Unable to submit form. Please try again or email your organizer directly at ${appConfig.contactUsEmail}`,
      });
    }
  }, [appConfig]);

  // useEffect(() => {
  //   if (orgId && eventId && errorMap) {
  //     validate();
  //   }
  // }, [eventId, emailParam, orgId, errorMap]);

  const verify = async () => {
    try {
      const result = await axios.get(
        `${process.env.REACT_APP_TNFT_SERVER}/dinner/verify`
      );
      if (result?.data?.success) {
        setVerified(true);
      } else {
        setVerifyErrorOpen(true);
      }
    } catch (e) {
      console.error("Not authorized");
    }
  };

  useEffect(() => {
    verify();
  }, []);

  const validate = async () => {
    const body = {
      email,
    };
    try {
      const result = await axios.post(
        `${process.env.REACT_APP_TNFT_SERVER}/dinner/validate`,
        body
      );
      if (result.data.success) {
        setErrorCode(null);
        setCanSubmit(true);
      }
    } catch (e) {
      const errorType = e?.response?.data?.errorType;
      if (!errorType) {
        console.log(e);
        return;
      }
      setErrorCode(errorType);
      setCanSubmit(false);
      return;
    }
  };

  const executeRecaptcha = async () => {
    if (!window.grecaptcha) {
      console.log("reCAPTCHA not yet available");
      return null;
    }

    return await window.grecaptcha.execute(
      process.env.REACT_APP_RECAPTCHA_SITE_KEY,
      { action: "dinner_submit" }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const recaptchaToken = await executeRecaptcha();
    if (!recaptchaToken) {
      console.log("reCAPTCHA failed or not ready");
      return;
    }
    const body = {
      email,
      recaptchaToken,
    };
    try {
      const result = await axios.post(
        `${process.env.REACT_APP_TNFT_SERVER}/dinner/submitform`,
        body
      );
      if (result.data?.success) {
        setSubmitted(true);
      }
    } catch (e) {
      setErrorCode("4001");
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 6 }}>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <img src={appStyle?.logo} height="175px" alt="Logo" />
      </Box>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Dialog open={verifyErrorOpen} maxWidth="xs" fullWidth>
          <DialogTitle>This page is not available</DialogTitle>
          <DialogContent>
            <Typography>
              Please access this form using the link provided by the event
              organizer. If you believe this is an error, contact the organizer
              for assistance.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVerifyErrorOpen(false)}>OK</Button>
          </DialogActions>
        </Dialog>
        <Typography variant="h5" gutterBottom>
          {!submitted ? "Ready to check in?" : "We're so happy you're here!"}
        </Typography>
        <Typography variant="body1" gutterBottom>
          {!submitted &&
            "Enter the email with which your purchased your ticket and then hit submit."}
        </Typography>
        {errorCode && errorMap?.[errorCode] && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMap[errorCode]}
          </Alert>
        )}

        {!submitted ? (
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              required
              label="Email"
              type="email"
              disabled={!verified}
              value={email}
              onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
              onBlur={validate}
              margin="normal"
            />
            <Button
              type="submit"
              variant="contained"
              disabled={!canSubmit}
              fullWidth
              sx={{ mt: 3 }}
            >
              Submit
            </Button>
          </Box>
        ) : (
          <>
            <Typography sx={{ mt: 2, fontWeight: 500 }}>
              Want to use the Guest List feature? Follow these steps:
            </Typography>

            <Box component="ul" sx={{ mt: 1, pl: 3 }}>
              <li>
                <Typography>
                  Click the <strong>Go to Login</strong> button below and sign
                  in to your account.
                </Typography>
              </li>
              <li>
                <Typography>
                  Make sure your profile is complete and that you have uploaded
                  a profile photo on the <strong>Profile</strong> tab.
                </Typography>
              </li>
              <li>
                <Typography>
                  Navigate to the <strong>My Events</strong> tab and click{" "}
                  <strong>Check In</strong>
                  for this event.
                </Typography>
              </li>
              <li>
                <Typography>
                  Once checked in, you will be able to view the Guest List and
                  see your compatibility scores with other attendees.
                </Typography>
              </li>
            </Box>
            <div style={{marginTop: 20, width: '100%', display: 'flex', justifyContent: 'center'}}> 
              <Button variant="outlined" onClick={() => navigate("/")}>Go To Login</Button>
            </div>
          </>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 3, display: "block" }}
        >
          This site is protected by reCAPTCHA and the Google{" "}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noreferrer"
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href="https://policies.google.com/terms"
            target="_blank"
            rel="noreferrer"
          >
            Terms of Service
          </a>{" "}
          apply.
        </Typography>
      </Paper>
    </Container>
  );
};

export default DinnerForm;
